<script setup>
import MonsterCalendar from '@/modules/monster-calendar-vue/dist/monster-calendar-vue';
import '@/modules/monster-calendar-vue/dist/style.css';

import { pickTypes, displayModes, rangeLockKeys } from '@/constants/calendar';

const props = defineProps({
  pickType: {
    type: String,
    default: pickTypes.single,
    validator(pickType) {
      return Object.values(pickTypes).includes(pickType);
    },
  },
  modelValue: {
    type: [String, Array],
    default: undefined,
  },

  displayMode: {
    type: String,
    default: displayModes.scroll,
    validator(displayMode) {
      return Object.values(displayModes).includes(displayMode);
    },
  },
  displayRangeMultiplier: {
    type: Number,
    default: 2,
  },
  rangeLock: {
    type: String,
    default: undefined,
    validator: (value) => Object.values(rangeLockKeys).includes(value),
  },

  isReturnSelecting: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits(['update:modelValue']);

const monthsCount = 25;

const modelValueLocal = useVModel(props, 'modelValue', emit);

const { locale } = useI18n();

// for displayMode: 'flat'
const isFlatDisplayMode = computed(
  () => props.displayMode === displayModes.flat,
);

// 'en-UK' locale to make Monday first day of week
const calendarLocale = computed(() =>
  locale.value === 'en' ? 'en-UK' : locale.value,
);
</script>

<template>
  <div
    class="ui-calendar"
    :class="[
      `ui-calendar--display-mode--${displayMode}`,
      { 'ui-calendar--return': isReturnSelecting },
    ]"
  >
    <MonsterCalendar
      v-model="modelValueLocal"
      class="ui-calendar__calendar"
      :months-count="monthsCount"
      :pick-type="pickType"
      :display-mode="displayMode"
      :locale="calendarLocale"
      :range-lock="rangeLock"
    >
      <template
        v-if="isFlatDisplayMode"
        #navigation="{ navigationState, changeMonthIndex }"
      >
        <button
          class="ui-calendar__button ui-calendar__button--navigation ui-calendar__button--navigation-prev"
          type="button"
          :disabled="!navigationState.prev"
          @click="changeMonthIndex(-1)"
        >
          <CIcon
            name="chevron-navigation"
            class="ui-calendar__icon ui-calendar__icon--navigation ui-calendar__icon--navigation-left"
            :class="{
              'ui-calendar__icon--navigation-disabled': !navigationState.prev,
            }"
          />
        </button>

        <button
          class="ui-calendar__button ui-calendar__button--navigation ui-calendar__button--navigation-next"
          type="button"
          :disabled="!navigationState.next"
          @click="changeMonthIndex(1)"
        >
          <CIcon
            name="chevron-navigation"
            class="ui-calendar__icon ui-calendar__icon--navigation ui-calendar__icon--navigation-right"
            :class="{
              'ui-calendar__icon--navigation-disabled': !navigationState.next,
            }"
          />
        </button>
      </template>
    </MonsterCalendar>
  </div>
</template>

<style scoped lang="scss">
.ui-calendar {
  $calendar-selector: '.monster-calendar';

  &:deep(#{$calendar-selector}) {
    position: relative;
    z-index: 1;
    gap: em(7);

    #{$calendar-selector} {
      &__font {
        display: block;

        &--week-day-name {
          font-size: em(10);
          line-height: math.div(14, 10);
          text-transform: uppercase;
        }

        &--month-name {
          font-size: em(15);
          font-weight: 500;
          line-height: math.div(24, 15);
        }

        &--day {
          position: relative;
          font-size: em(14);
          font-weight: 600;
          line-height: math.div(19, 14);
        }
      }

      &__display {
        &--type {
          &--flat {
            display: flex;
            gap: em(10);
            align-items: center;

            #{$calendar-selector}__wrapper {
              flex-grow: 1;
              gap: em(20);
              min-height: em(336);
            }
          }

          &--scroll {
            #{$calendar-selector}__font {
              &--month-name {
                font-size: em(20);
                font-weight: 500;
                line-height: math.div(24, 20);
              }
            }

            #{$calendar-selector}__month {
              padding-top: em(5);
              border: 1px solid $color-gray-8;
              border-radius: em(8);
            }
          }
        }
      }

      &__week-day-names {
        @include custom-scrollbar-transparent;
      }

      &__text {
        &--week-day-name {
          display: flex;
          align-items: center;
          justify-content: center;
          height: em(35);
          color: $color-gray-4;
          text-align: center;
        }

        &--week-day-weekend {
          color: $color-purple-2;
        }

        &--month-name {
          padding-left: em(14);
          color: $color-third-1;
        }
      }

      &__months {
        gap: em(15);
      }

      &__month {
        gap: em(7);
      }

      &__day {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        height: em(45);
        color: $color-black;
        text-align: center;
        border: 1px solid transparent;
        border-radius: em(8);
        transition: border-color $time-3;

        &:disabled {
          color: $color-gray-17;
        }

        @include hover {
          &::after {
            position: absolute;
            inset: -1px;
            display: block;
            content: '';
            border: em(2) solid $color-main-5;
            border-radius: em(8);
          }
        }

        &--today {
          color: $color-black;
          border: 1px solid $color-gray-8;
        }

        &--foreign {
          pointer-events: none;

          // color: $color-gray-4;
          opacity: 0;
        }

        &--weekend {
          color: $color-purple-2;
        }
      }
    }

    &#{$calendar-selector} {
      &--type {
        &--single {
          #{$calendar-selector}__day {
            &--selected {
              color: $color-white;
              background-color: $color-main-5;
              border-radius: em(8);
            }
          }
        }

        &--range {
          #{$calendar-selector}__day {
            &--selected {
              background-color: $color-main-7;
              border-radius: unset;
              transition: none;

              &:last-child,
              &#{$calendar-selector}__day--last {
                border-top-right-radius: em(8);
                border-bottom-right-radius: em(8);
              }

              &:first-child,
              &#{$calendar-selector}__day--first {
                border-top-left-radius: em(8);
                border-bottom-left-radius: em(8);
              }
            }

            &--range-start {
              color: $color-white;
              background-color: $color-main-5;
              border-radius: em(8);

              &#{$calendar-selector}__day--range-end {
                &::before {
                  position: absolute;
                  top: 0;
                  left: 0;
                  display: block;
                  width: 50%;
                  height: 100%;
                  content: '';
                  background-color: $color-main-5;
                  border-radius: em(8) 0 0 em(8);
                }
              }
            }

            &--range-hover {
              background-color: $color-main-7;
              border-radius: unset;
              transition: none;

              &:last-child {
                border-top-right-radius: em(8);
                border-bottom-right-radius: em(8);
              }

              &:first-child {
                border-top-left-radius: em(8);
                border-bottom-left-radius: em(8);
              }
            }

            &--range-end {
              color: $color-white;
              background-color: $color-second-1;
              border-radius: em(8);
            }
          }
        }
      }

      &--complete {
        &#{$calendar-selector}--type--range {
          #{$calendar-selector}__day {
            &--range-start {
              &:not(
                  #{$calendar-selector}__day--range-end,
                  #{$calendar-selector}__day--last,
                  :last-child
                )::after {
                position: absolute;
                top: 0;
                right: 0;
                z-index: -1;
                display: block;
                width: 50%;
                height: 100%;
                content: '';
                background-color: $color-main-7;
              }
            }

            &--range-end {
              &:not(
                  #{$calendar-selector}__day--range-start,
                  #{$calendar-selector}__day--first,
                  :first-child
                )::before {
                position: absolute;
                top: 0;
                left: 0;
                z-index: -1;
                display: block;
                width: 50%;
                height: 100%;
                content: '';
                background-color: $color-main-7;
              }
            }
          }
        }
      }
    }
  }

  &__icon {
    flex-shrink: 0;

    &--navigation {
      width: em(24);
      height: em(24);
      color: $color-main-5;
      transition: opacity $time-3;
    }

    &--navigation-right {
      transform: scaleX(-1);
    }

    &--navigation-disabled {
      opacity: 0.25;
    }
  }

  &__button {
    &--navigation {
      padding: em(10);
    }

    &--navigation-prev {
      order: -1;
    }
  }

  &__calendar {
    flex-grow: 1;
  }

  &--display-mode {
    &--scroll {
      display: flex;
      flex-direction: column;
    }
  }

  &--return {
    &:deep(#{$calendar-selector}) {
      #{$calendar-selector} {
        &__day {
          @include hover {
            &::after {
              border-color: $color-second-1;
            }
          }
        }
      }
    }
  }
}
</style>
